import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yeshuman/sdk'
import { useEffect, useState } from 'react'
import {
  type ConditionItem,
  type ConditionClinicalStatus,
  type CreateConditionData,
  type UpdateConditionData,
} from '../hooks/use-conditions'
import { CONDITION_STATUS_LABELS } from './condition-card'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface ConditionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  condition?: ConditionItem | null
  onCreate: (data: CreateConditionData) => Promise<void>
  onUpdate: (data: UpdateConditionData) => Promise<void>
}

const STATUS_OPTIONS: ConditionClinicalStatus[] = ['active', 'monitoring', 'remission', 'resolved']

export function ConditionFormDialog({
  open,
  onOpenChange,
  condition,
  onCreate,
  onUpdate,
}: ConditionFormDialogProps) {
  const [name, setName] = useState('')
  const [clinicalStatus, setClinicalStatus] = useState<ConditionClinicalStatus>('active')
  const [onsetDate, setOnsetDate] = useState('')
  const [abatementDate, setAbatementDate] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    if (condition) {
      setName(condition.name)
      setClinicalStatus(condition.clinical_status)
      setOnsetDate(condition.onset_date ?? '')
      setAbatementDate(condition.abatement_date ?? '')
      setNotes(condition.notes ?? '')
    } else {
      setName('')
      setClinicalStatus('active')
      setOnsetDate('')
      setAbatementDate('')
      setNotes('')
    }
  }, [condition, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Name is required')
      return
    }
    setIsSubmitting(true)
    try {
      if (condition) {
        const update: UpdateConditionData = {
          condition_id: condition.id,
          name: name.trim(),
          clinical_status: clinicalStatus,
          onset_date: onsetDate || null,
          abatement_date: abatementDate || null,
          notes,
        }
        await onUpdate(update)
        toast.success('Condition updated')
      } else {
        await onCreate({
          name: name.trim(),
          clinical_status: clinicalStatus,
          onset_date: onsetDate || undefined,
          notes: notes || undefined,
        })
        toast.success('Condition saved')
      }
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{condition ? 'Edit condition' : 'Track a condition'}</DialogTitle>
          <DialogDescription>
            Record a condition you're managing, in your own words. This is a self-declared record —
            not a diagnosis. Talk to a qualified practitioner about diagnosis or treatment.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="condition-name">Condition</Label>
            <Input
              id="condition-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. asthma, migraine"
              className="placeholder:italic"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={clinicalStatus}
              onValueChange={(v) => setClinicalStatus(v as ConditionClinicalStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {CONDITION_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="condition-onset">Since (optional)</Label>
            <Input
              id="condition-onset"
              type="date"
              value={onsetDate}
              onChange={(e) => setOnsetDate(e.target.value)}
            />
          </div>

          {condition && (clinicalStatus === 'remission' || clinicalStatus === 'resolved') ? (
            <div className="space-y-2">
              <Label htmlFor="condition-abatement">Resolved on (optional)</Label>
              <Input
                id="condition-abatement"
                type="date"
                value={abatementDate}
                onChange={(e) => setAbatementDate(e.target.value)}
              />
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="condition-notes">Notes</Label>
            <Textarea
              id="condition-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="How it affects you, what you're doing to manage it…"
              className="placeholder:italic"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
